import type { FuseResultMatch } from 'fuse.js';
import { isEmpty, keyBy } from 'lodash-es';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { SearchGroupMember } from '@/hooks/search/use-search-group-members';
import type { Group } from '@/models/group/group-info';
import ROUTES from '@/routes';
import intersperse from '@/utils/intersperse';
import cn from '@/utils/merge-class-names';
import { generateSessionKey } from '@/utils/session';
import SearchListItem, { HighlightKeyword } from './search-list-item';

interface Props {
  groups: (Group & {
    FuzzySearch: {
      highlightRanges?: readonly FuseResultMatch[];
      score?: number;
    };
  })[];
  groupMembers: SearchGroupMember[];
  keyword?: string;
  className?: string;
}

export default function SearchGroupResultList({ className, keyword = '', groups, groupMembers }: Props) {
  const { t } = useTranslation();

  const mergedGroups = useMemo(() => {
    if (!groupMembers)
      return groups.map((g) => ({
        GroupID: g.ID,
        FuzzySearch: g.FuzzySearch,
        GroupName: g.GroupName,
        GroupImg: g.GroupImg,
        Users: [],
      }));
    if (!groups) return groupMembers;
    const filteredMap = keyBy(groups, 'ID');

    const matchedGroupMembers = groupMembers.map((groupMembers) => {
      const f = filteredMap[groupMembers.GroupID];
      return f ? { ...groupMembers, FuzzySearch: f.FuzzySearch } : groupMembers;
    });

    const rest = groups
      .filter(({ ID }) => !groupMembers.some(({ GroupID }) => ID === GroupID))
      .map((g) => ({
        GroupID: g.ID,
        FuzzySearch: g.FuzzySearch,
        GroupName: g.GroupName,
        GroupImg: g.GroupImg,
        Users: [],
      }));

    return [...matchedGroupMembers, ...rest];
  }, [groupMembers, groups]);

  if (isEmpty(mergedGroups)) return null;

  return (
    <div className={cn('group/group', className)}>
      <h4 className='flex h-6 group-first/group:mt-1 mb-2 items-center px-5 text-body1 font-bold'>
        {t('search.groups')}
      </h4>

      {mergedGroups.map(({ GroupID, GroupImg, GroupName, Users }) => (
        <Link
          key={generateSessionKey('group', GroupID)}
          to={ROUTES.MESSAGES.GROUPS.$buildPath({ params: { id: GroupID } })}
          state={ROUTES.MESSAGES.GROUPS.$buildState({ state: { from: 'search' } })}>
          <SearchListItem
            avatarImageUrl={GroupImg}
            highlight={keyword}
            highlightField='title'
            title={GroupName}
            description={
              !isEmpty(Users) &&
              t('search.include', {
                names: intersperse<ReactNode>(
                  Users?.map(({ MatchedName }) => (
                    <HighlightKeyword
                      key={generateSessionKey('group', GroupID) + MatchedName}
                      text={MatchedName}
                      highlight={keyword}
                    />
                  )),
                  <span>{', '}</span>,
                ),
              })
            }
          />
        </Link>
      ))}
    </div>
  );
}
